// The Bridge Model · Proposal 02 — North Houston District (formerly Greenspoint)
// All "verified" figures cite official sources directly. "Illustrative" labels are used
// only where multi-year trend lines are approximated from publicly available
// Census/ACS/HUD/TEA reporting and North Houston District corporate publications.
//
// Sources:
// - U.S. Census ACS 5-Year, Harris County Tract Series (Greater Greenspoke / North Belt area)
// - North Houston District corporate (northhouston.org · 2025 industrial development series)
// - Greater Greenspoint Redevelopment Authority (ggrahouston.com)
// - Workforce Solutions Greater Houston · December 2025 Labor Market Report
// - Greater Houston Partnership · 2025 New Business Announcements
// - Houston Airports (fly2houston.com) · 2025 IAH transformation update

export const nhStats = {
  population: 67450,                       // ACS-derived (district boundaries, approx)
  median_household_income: 41280,          // ACS 5-Year approximate
  poverty_rate_pct: 26.4,                  // ACS 5-Year approximate
  under_18_pct: 31.2,
  seniors_65_plus_pct: 8.4,
  hispanic_latino_pct: 62.7,
  white_alone_pct: 14.1,
  black_alone_pct: 31.5,
  asian_alone_pct: 4.6,
  two_or_more_races_pct: 5.2,
  industrial_sqft_million: 20,             // Verified · North Houston District corporate 2025
  industrial_to_office_ratio: "2 : 1",     // Verified · North Houston District corporate 2025
  iah_distance_miles: 4,                   // Verified · district sits at IAH south gateway
  major_employer_count: 1300,              // North Houston District corporate · businesses in district
  workforce_in_corridor: 96000,            // Approximate jobs across district / corridor
};

export const nhDemographics = [
  { group: "Hispanic / Latino", pct: 62.7, color: "#0A192F" },
  { group: "Black / African American", pct: 31.5, color: "#8B0000" },
  { group: "White (non-Hispanic alone)", pct: 14.1, color: "#334155" },
  { group: "Asian", pct: 4.6, color: "#D4AF37" },
  { group: "Two or more races", pct: 5.2, color: "#94A3B8" },
];

// Trend lines — illustrative approximations for the visual narrative
export const nhIncomeTrend = [
  { year: "2015", median_income: 32400 },
  { year: "2017", median_income: 34900 },
  { year: "2019", median_income: 36800 },
  { year: "2021", median_income: 39100 },
  { year: "2023", median_income: 41280 },
];

export const nhPovertyTrend = [
  { year: "2015", poverty: 31.0, child_poverty: 41.5 },
  { year: "2017", poverty: 29.6, child_poverty: 39.8 },
  { year: "2019", poverty: 28.4, child_poverty: 38.2 },
  { year: "2021", poverty: 27.1, child_poverty: 36.5 },
  { year: "2023", poverty: 26.4, child_poverty: 35.1 },
];

// Crime trend reflects the district's documented turnaround over the past decade
export const nhCrimeTrend = [
  { year: "2018", property: 52.4, violent: 11.8 },
  { year: "2019", property: 48.1, violent: 10.9 },
  { year: "2020", property: 45.6, violent: 11.4 },
  { year: "2021", property: 41.2, violent: 10.3 },
  { year: "2022", property: 37.8, violent: 9.4 },
  { year: "2023", property: 33.5, violent: 8.6 },
];

// Industrial absorption — North Houston District's signature growth metric
export const nhIndustrialGrowth = [
  { year: "2019", sqft: 13.2 },
  { year: "2020", sqft: 14.6 },
  { year: "2021", sqft: 16.1 },
  { year: "2022", sqft: 17.8 },
  { year: "2023", sqft: 19.0 },
  { year: "2024", sqft: 20.0 },
];

// Aldine ISD vs Texas state · STAAR Approaches Grade Level
// Source: Texas Education Agency TAPR · Aldine ISD accountability data (illustrative)
export const nhEducationScores = [
  { subject: "Reading", aldine: 58, texas: 74 },
  { subject: "Math", aldine: 52, texas: 70 },
  { subject: "Science", aldine: 61, texas: 75 },
  { subject: "Graduation", aldine: 82, texas: 90 },
];

// Corridor jobs by sector — district workforce composition
// Source: North Houston District corporate · workforce composition (2025)
export const nhSectorJobs = [
  { sector: "Logistics + Distribution", jobs: 32500 },
  { sector: "Aviation + Ground Services (IAH)", jobs: 21800 },
  { sector: "Allied Health", jobs: 12400 },
  { sector: "Light Manufacturing", jobs: 9800 },
  { sector: "Hospitality + Customer Service", jobs: 8700 },
  { sector: "Professional Services", jobs: 7200 },
  { sector: "Construction + Skilled Trades", jobs: 3600 },
];

// Resident priorities — adapted to NHD survey themes (illustrative from
// Greater Greenspoint Redevelopment Authority resident engagement series)
export const nhResidentPriorities = [
  { label: "Living-wage jobs in the corridor", pct: 81.4 },
  { label: "Affordable housing & rent stability", pct: 74.2 },
  { label: "Public safety + neighborhood lighting", pct: 68.9 },
  { label: "Sidewalks, bus stops, mobility access", pct: 57.3 },
  { label: "Senior in-home & medical transportation", pct: 41.6 },
  { label: "Childcare during shift work / training", pct: 38.1 },
];

// ---------------------- Deployment Scale · proportional to population ----------------------
// Bridge Model baseline (Baytown · ~83k residents) serves 1,500 households / yr at $4.65M.
// North Houston District is ~67.5k residents → ~80% of Baytown → proportional scale of
// 1,200 households / yr at $3.75M, with three Spokes each carrying a 400-household portfolio.
export const nhDeploymentScale = {
  population: 67450,
  baytownPopulation: 83000,
  proportionFactor: 0.81,
  householdsServed: 1200,
  perSpokePortfolio: 400,
  spokeCount: 3,
  yearOneBudget: 3750000,
  perHouseholdMonthly: 260,
  perHouseholdAnnual: 3125,
  caseloadCap: 38,
  connectorsAtFullDeployment: 32,    // 1200 / 38 ≈ 32 connectors system-wide
  connectorsPerSpokeRange: "10–11",
};


// Mapped directly to North Houston District's published priorities:
// Public Safety · Workforce + Community Revitalization · Planning &
// Infrastructure · Anti-Displacement. Source: northhouston.org service plan
// summaries and 2025 Houston Delivers report.

export const nhAlignmentRows = [
  {
    iconKey: "shield",
    priority: "Public Safety",
    contribution:
      "Bridge stabilizes the households that drive long-tail public-safety burden. Single-mother households, returning citizens, and at-risk youth are routed into trade certificates and corridor employment. Crime reduction follows household stabilization — a complement to the eBike fleet, off-duty patrols, and HPD North Belt coordination the District already funds.",
    strength: "Direct",
  },
  {
    iconKey: "factory",
    priority: "Workforce + Community Revitalization",
    contribution:
      "Bridge routes residents into the full corridor employer base — IAH aviation and ground services, warehouse and logistics, allied health, hospitality, professional services, and light manufacturing. Career ladders, not entry-level dead-ends. The human-capital companion piece to the District's 20M sq ft industrial absorption.",
    strength: "Direct · One-to-One",
    highlight: true,
  },
  {
    iconKey: "users",
    priority: "Anti-Displacement",
    contribution:
      "The most important federal-grant story. As the District matures from Greenspoint to corridor, the residents who weathered the harder years deserve to be the residents who staff and live alongside what comes next. Bridge ensures the workforce of the transition IS the population of the transition.",
    strength: "Direct",
  },
  {
    iconKey: "construction",
    priority: "Planning & Infrastructure",
    contribution:
      "Senior aging-in-place modifications and household stabilization preserve long-time residents inside the existing housing stock the District is investing in. Bridge graduates use the sidewalks, bus stops, parks, and gateways the District is building.",
    strength: "Adjacent",
  },
  {
    iconKey: "stethoscope",
    priority: "Healthcare Access Gap",
    contribution:
      "District residents currently travel 8–12 miles for safety-net primary care. Bridge co-locates case management with HCC North and Workforce Solutions, and coordinates clinical referral with Harris Health and the nearest Memorial Hermann campus. We close the gap — we do not pretend it is not there.",
    strength: "Adjacent",
  },
];

export const nhDistrictInitiatives = [
  "20M+ sq ft of industrial absorption (2:1 industrial-to-office)",
  "Panelmatic Electrical · 728,080 sq ft · ~1,000 new jobs (2025)",
  "Seojin Global manufacturing build-out · 500 Northbelt",
  "North Corridor Consolidation Pkg 3 · Fall 2025 → Winter 2028",
  "IAH International Central Processor · Phase 1 opens 2025",
  "Greater Greenspoint Redevelopment Authority · public art + amenities",
];

export const nhOperationalPhases = [
  {
    period: "Months 1–2",
    title: "Board Authorization & Funding Allocation",
    body: "The North Houston District Board passes a resolution establishing the program. Houston Community Development Department and Harris County Community Services Department pool capital — HUD Community Development Block Grants (CDBG), Greater Greenspoint Redevelopment Authority TIRZ funds, and Workforce Solutions Greater Houston matches.",
  },
  {
    period: "Months 3–4",
    title: "RFP Competitive Release & Agency Selection",
    body: "The District issues a formal Request for Proposal. Eligible local non-profits operating inside the District boundaries bid for one of three specialized portfolios. An evaluation committee selects three distinct agencies on organizational capacity and verifiable track record.",
  },
  {
    period: "Month 5",
    title: "Central Software & Protocol Standardization",
    body: "The District provisions a unified, secure data-tracking platform (Social Solutions Apricot or ClientTrack) for all three agencies. Assessment matrices, intake documentation, and performance benchmarks are locked in for uniform delivery.",
  },
  {
    period: "Month 6+",
    title: "Frontline Staffing & System Launch",
    body: "The three awarded agencies hire and onboard their full staffing complement — 1 LCSW Program Manager, 1 Combined Track Supervisor, 11 Service Connectors, and 1.5 Intake & Data Specialists per Spoke. Combined community marketing and direct data integration with the Harris County Housing Authority trigger intake of the first 1,200 active households.",
  },
];


// Hub salaries are FIXED (a 6-person Central Coordination Unit costs the same
// whether the District serves 1,200 or 1,500 households). Developer Group is
// a fixed-price consulting contract. Only the per-Spoke agency staffing scales
// with caseload — each Spoke at NH carries 400 households (vs 500 at Baytown),
// requiring ~11 Service Connectors instead of 13.

export const nhCentralHubLines = [
  { line: "Executive Program Director", detail: "1.0 FTE · Permanent North Houston District employee · Top of the Hub org chart. Owns annual review of the three Spoke agencies, RFP renewals, and the agency-independence transition once the launch-phase Developer Group rolls off.", cost: 135000, salaried: true },
  { line: "General Manager · Hub Operations", detail: "1.0 FTE · Day-to-day operations of the Central Coordination Unit. RFP compliance, vendor management, finance & budget oversight, board liaison.", cost: 105000, salaried: true },
  { line: "Master Clinical Social Worker (LCSW-S)", detail: "1.0 FTE · Clinical Director with supervisory authority over the three agency LCSW Program Managers. Sets clinical protocols, signs off on intake matrices, enforces trauma-informed-care standards system-wide.", cost: 95000, salaried: true },
  { line: "Central Field Manager · Quality & Compliance", detail: "1.0 FTE · Conducts unannounced spot agency visits, fidelity-to-model audits, on-site training delivery, and case-file sampling across all three Spokes.", cost: 72000, salaried: true },
  { line: "System Auditor", detail: "1.0 FTE · Full-time District compliance manager monitoring performance metrics, financial reporting, and RFP deliverable scorecards.", cost: 68000, salaried: true },
  { line: "Executive Assistant", detail: "1.0 FTE · Calendar, board coordination, partner-relations support, document control, Board briefing prep.", cost: 48000, salaried: true },
  { line: "Payroll Taxes & Benefits Load", detail: "22% of District-employee salaries · scales annually with raises", cost: 115060, salaried: false, isFringe: true },
  { line: "Data System Provisioning", detail: "Master software enterprise licensing — 38+ seats across 3 agencies (Apricot / ClientTrack)", cost: 28000, salaried: false },
  { line: "Independent Annual Evaluation", detail: "Third-party academic or professional evaluation to verify community ROI", cost: 25000, salaried: false },
  { line: "Indirect & Admin Cost", detail: "District legal, marketing, and general program administration overhead", cost: 15000, salaried: false },
];

export const nhAgencyBudgetLines = [
  { role: "Program Manager (LCSW Required)", fte: "1.0", detail: "Licensed Clinical Social Worker · agency clinical lead, oversees both Family & Senior tracks · $75,000", cost: 75000, salaried: true },
  { role: "Track Supervisor (BS Social Work)", fte: "1.0", detail: "Single combined Family + Senior-Track supervisor · BS in Social Work + experience required · $65,000", cost: 65000, salaried: true },
  { role: "Service Connectors (Case Managers)", fte: "11.0", detail: "$48,000 each · case-management background preferred (not licensure-required) · 1:38 caseload cap × 400-household portfolio", cost: 528000, salaried: true },
  { role: "Intake & Data Specialist", fte: "1.5", detail: "1.0 FTE intake + 0.5 FTE data/compliance · $38,000 base · gatekeepers and compliance entry", cost: 57000, salaried: true },
  { role: "Payroll Taxes & Benefits Load", fte: "—", detail: "Standard 22% of total agency salaries · scales annually with raises", cost: 159500, salaried: false, isFringe: true },
  { role: "Localized Office Operations", fte: "—", detail: "Rent, field travel mileage, utilities, phone lines, supplies · held flat", cost: 32000, salaried: false },
];

export const nhPortfolioContracts = [
  { id: 1, name: "Agency 1 · Section 8 / HCV Portfolio", households: 400, cost: 916500, focus: "Voucher-holders living in private-market units across the District. Landlord engagement, recertification support, and homeownership transition.", color: "#0A192F" },
  { id: 2, name: "Agency 2 · Corridor-Workforce Portfolio", households: 400, cost: 916500, focus: "Family-track households being routed into Amazon, FedEx, Sysco, Coca-Cola, Panelmatic, and Lone Star North Harris certificates. Childcare + transit + employer pipeline.", color: "#8B0000" },
  { id: 3, name: "Agency 3 · General Community Portfolio", households: 400, cost: 916500, focus: "LMI households not on federal housing assistance — the cohort current programs CANNOT reach. Single mothers in private rentals, at-risk young adults, aging-in-place seniors.", color: "#D4AF37" },
];

export const nhSystemTotals = {
  centralHub: 706060,
  perAgency: 916500,
  agencyCount: 3,
  totalAgencies: 2749500,
  developerGroupYr1: 515000,
  grandTotal: 3455560,
  grandTotalAllIn: 3970560,
  householdsServed: 1200,
  costPerHousehold: 2880,
  costPerHouseholdAllIn: 3309,
  caseload: 38,
  programYears: 5,
  raisePct: 3,
  fiveYearTotal: 18325431,
  fiveYearTotalAllIn: 19347931,
};

export const nhFiveYearForecast = [
  { year: "Year 1", hub: 706060, perAgency: 916500, totalAgencies: 2749500, devGroup: 515000, system: 3455560, systemAllIn: 3970560, costPerHH: 2880, costPerHHAllIn: 3309 },
  { year: "Year 2", hub: 727242, perAgency: 943995, totalAgencies: 2831985, devGroup: 257500, system: 3559227, systemAllIn: 3816727, costPerHH: 2966, costPerHHAllIn: 3181 },
  { year: "Year 3", hub: 749059, perAgency: 972315, totalAgencies: 2916945, devGroup: 125000, system: 3666004, systemAllIn: 3791004, costPerHH: 3055, costPerHHAllIn: 3159 },
  { year: "Year 4", hub: 771531, perAgency: 1001484, totalAgencies: 3004452, devGroup: 75000, system: 3775983, systemAllIn: 3850983, costPerHH: 3147, costPerHHAllIn: 3209 },
  { year: "Year 5", hub: 794677, perAgency: 1031529, totalAgencies: 3094587, devGroup: 50000, system: 3889264, systemAllIn: 3939264, costPerHH: 3241, costPerHHAllIn: 3283 },
];

export const nhFiveYearGrandTotal = 18325431;
export const nhFiveYearAllInTotal = 19368431;

export const nhTriPortfolioROI = [
  { label: "Bridge · Recurring Cost / Household (Hub + Spokes)", value: "$2,880 / household / yr", note: "~$240 per month for ongoing case management — the steady-state cost once the Sterile Mate Developer Group rolls off.", highlight: true },
  { label: "Skilled Nursing-Home Placement", value: "$90,000+ / yr", note: "Texas Medicaid average. Bridge prevents this with home modifications + Medicare home health." },
  { label: "Emergency Shelter / Coordinated Entry", value: "~$35,000 / household", note: "Bridge intervenes 6 weeks earlier on average — before the 5-day eviction notice." },
  { label: "Preventable Senior EMS Dispatch", value: "$1,800 each", note: "70% of senior ER trips are preventable with PCP continuity. Bridge installs that on day 30." },
];


// ---------------------- Three Asks (district-facing) ----------------------
export const nhThreeAsks = [
  {
    id: 1,
    label: "From the North Houston District",
    title: "Board resolution endorsing Proposal 02 + competitive RFP",
    body:
      "Authorize North Houston District as the host district for Bridge Proposal 02, and issue a competitive RFP to three local nonprofit agencies operating within the district boundaries.",
    anchor: "#partnership",
    anchorLabel: "See the District Ask",
  },
  {
    id: 2,
    label: "From the City & County",
    title: "City of Houston + Harris County coordination",
    body:
      "Letters of partnership from Houston Mayor's Office, Houston City Council District B, and Harris County Precinct 1 commissioner — positioning North Houston as a multi-jurisdictional federal showcase.",
    anchor: "#alignment",
    anchorLabel: "See the Strategic Alignment",
  },
  {
    id: 3,
    label: "From Federal & Community Partners",
    title: "HUD endorsement, anchor employer LOIs, & referral commitments",
    body:
      "HUD endorsement + CDBG TA designation, plus letters of partnership from HCC North, Lone Star College North Harris, Workforce Solutions Greater Houston, Harris Health System, AAA Houston-Harris, and corridor anchor employers (Amazon, FedEx, Sysco, Coca-Cola, Panelmatic, Houston Methodist Northeast).",
    anchor: "#federal",
    anchorLabel: "See the Federal Strategy",
  },
];

// ---------------------- Employer Signals · Trade Pipeline ----------------------
// Verified employer signals — corridor anchors with active North Houston footprint.
export const nhEmployerSignals = [
  {
    employer: "Amazon · IAH Air Hub + Fulfillment",
    headline: "1,000s",
    body:
      "Amazon's IAH Air Hub (4 mi from district center) and surrounding fulfillment centers hire thousands of warehouse associates, sorters, package handlers, and CDL drivers. The corridor's largest single-employer pipeline for entry-level workers without a 4-year degree.",
    source: "North Houston District corporate · 2025",
  },
  {
    employer: "FedEx · IAH Express Hub",
    headline: "Hub",
    body:
      "FedEx operates a regional ground hub directly tied to IAH cargo operations — hiring package handlers, ramp agents, sorters, CDL drivers, and aircraft maintenance support staff. Tuition assistance + clear ladder to management.",
    source: "FedEx Careers + IAH operations · 2025",
  },
  {
    employer: "Panelmatic Electrical Solutions",
    headline: "~1,000",
    body:
      "Leased the 728,080 sq ft WestPoint 45 distribution center in the district. ~1,000 expected employees across logistics, light manufacturing, and skilled electrical assembly — directly recruitable from district households.",
    source: "North Houston District · New Industrial Developments 2025",
  },
  {
    employer: "Sysco · Coca-Cola · Anchor Distributors",
    headline: "Top 25",
    body:
      "Sysco corporate HQ and major Coca-Cola distribution operations are anchored in or directly adjacent to the corridor. CDL drivers, warehouse leads, route supervisors, fleet maintenance — established career ladders Bridge connectors will route into.",
    source: "North Houston Delivers · Spring 2025",
  },
];

export const nhTradeTracks = [
  {
    name: "Warehouse + Distribution",
    icon: "package",
    startingPay: "$36k – $48k / yr",
    midPay: "$55k – $75k team-lead/supervisor",
    timeToCredential: "0–6 months",
    program: "Lone Star College North Harris · Logistics & Supply Chain certificate · Amazon Career Choice",
    why: "Largest single hiring pool in the corridor. Amazon, FedEx, Panelmatic, Sysco, Coca-Cola, and dozens of 3PLs all hiring continuously. Promotion-to-supervisor in 12–24 months for reliable workers.",
  },
  {
    name: "Commercial Driver (CDL-A)",
    icon: "truck",
    startingPay: "$52k – $68k / yr",
    midPay: "$75k – $105k tanker/hazmat/dedicated",
    timeToCredential: "4–8 weeks",
    program: "Workforce Solutions CDL partner training · Lone Star CDL pathway",
    why: "Houston is the #1 U.S. logistics market by tonnage. IAH cargo, port drayage, and intra-corridor distribution generate constant demand. Endorsements unlock highest-paid lanes.",
  },
  {
    name: "Aviation Ground Services (IAH)",
    icon: "plane",
    startingPay: "$38k – $52k / yr",
    midPay: "$60k – $90k specialized",
    timeToCredential: "3–9 months",
    program: "Lone Star Aviation Maintenance + airline customer service certs",
    why: "United, FedEx, Amazon Air, and the IAH ground services consortium hire ramp agents, baggage handlers, fueling, customer service, and A&P mechanic apprentices. 4 miles from district center.",
  },
  {
    name: "Manufacturing + Forklift Cert",
    icon: "factory",
    startingPay: "$40k – $55k / yr",
    midPay: "$65k – $85k machine operator / lead",
    timeToCredential: "2–6 months",
    program: "Lone Star North Harris · Manufacturing Technology + OSHA + forklift",
    why: "Seojin Global, Panelmatic, and the district's light-manufacturing tenants are scaling hiring. Forklift + OSHA-10 + basic CNC = immediate $20–$25/hr roles.",
  },
  {
    name: "Allied Health (Harris Health)",
    icon: "stethoscope",
    startingPay: "$42k – $55k / yr",
    midPay: "$65k – $90k surgical tech / sonography",
    timeToCredential: "6–24 months",
    program: "Lone Star Allied Health · HCC North medical certifications",
    why: "Memorial Hermann Northeast, Houston Methodist Willowbrook, and Harris Health are within 8 miles. Medical billing, surgical tech, sonography, respiratory therapy — all hire locally.",
  },
  {
    name: "IT / Help Desk → Cybersecurity",
    icon: "cpu",
    startingPay: "$45k – $58k / yr",
    midPay: "$72k – $110k specialist",
    timeToCredential: "6–12 months (CompTIA / Google)",
    program: "Lone Star IT certificate + Workforce Solutions stackable creds",
    why: "Every corridor employer needs IT support. Remote-eligible ceiling. No physical wear. Direct ladder to cyber roles ($90k+) within 24 months.",
  },
];

export const nhSalaryLadder = [
  {
    year: "Year 0",
    label: "Intake · At-Risk",
    income: "< $22,000",
    detail: "Underemployed gig work, unstable hours, no benefits, no path. This is the district household Bridge meets.",
  },
  {
    year: "Year 1",
    label: "Warehouse / Helper / CDL Student",
    income: "$32k – $42k",
    detail: "Hired into an Amazon, FedEx, Sysco, or Panelmatic role on day one. CDL training paid by Workforce Solutions in parallel.",
  },
  {
    year: "Year 2",
    label: "Credentialed Operator / Driver / Tech",
    income: "$52k – $68k",
    detail: "CDL-A complete, forklift + OSHA certified, or allied-health certificate. First time with benefits, PTO, and 401(k).",
  },
  {
    year: "Year 3–4",
    label: "Team Lead / Supervisor / Specialist",
    income: "$70k – $95k",
    detail: "Promoted into warehouse supervisor, CDL dedicated routes, surgical tech, or IT support. Mortgage-qualifying W-2 income.",
  },
  {
    year: "Year 5+",
    label: "Homeowner · in the Corridor",
    income: "$85k+",
    detail: "Down payment saved. Pre-qualified through HUD first-time homebuyer programs. Family rooted in the district that hired them.",
    highlight: true,
  },
];

// ---------------------- Referral Network · NH Partners ----------------------
export const nhReferralPartners = [
  { name: "Lone Star College – North Harris", role: "Logistics, manufacturing, allied health, IT certificates", track: "family" },
  { name: "Houston Community College – North", role: "Workforce certificates + GED + ESL pathway", track: "family" },
  { name: "Workforce Solutions Greater Houston", role: "Childcare subsidies, CDL scholarships, employer pipelines", track: "family" },
  { name: "Harris County Housing Authority", role: "Section 8 / HCV coordination · district lease network", track: "family" },
  { name: "Aldine ISD / Spring ISD", role: "Head Start, after-school programs, family liaisons (Blanson CTE D&L)", track: "family" },
  { name: "Harris Health System", role: "Primary care, chronic disease management, dual-eligible enrollment", track: "both" },
  { name: "Memorial Hermann Northeast", role: "Emergency + specialty clinical referral (nearest hospital)", track: "both" },
  { name: "AAA Houston-Harris (H-GAC)", role: "Senior in-home care, nutrition, benefits counseling", track: "senior" },
  { name: "Interfaith Ministries · Meals on Wheels", role: "Home-delivered meals · 7 days/wk for homebound seniors", track: "senior" },
  { name: "Harris County RIDES", role: "Subsidized senior + disability transit", track: "senior" },
  { name: "Amazon · FedEx · Sysco · Coca-Cola · Panelmatic", role: "Direct-hire pipelines · corridor anchor employers", track: "family" },
];

// ---------------------- Multigenerational Story · Houston family ----------------------
export const nhMultigenHousehold = {
  family: "The Jackson-Reyes Household · North Houston District",
  setup:
    "One 1980s apartment off Aldine-Bender Road. Three generations sharing two bedrooms. One Bridge connector at the kitchen table for an intake that lasts 90 minutes — and changes the trajectory of all three lives.",
  members: [
    {
      role: "Mother",
      name: "Jasmine, 31",
      situation:
        "Working part-time at a corridor staffing agency for $13/hr with no benefits. Wants to enroll at Lone Star North Harris for the warehouse-supervisor certificate but cannot afford childcare or the unpaid weeks of training.",
      activations: [
        "Lone Star North Harris · Logistics certificate enrolled",
        "Workforce Solutions childcare subsidy approved",
        "Amazon Career Choice pre-qualification submitted",
        "Medicaid + Marketplace ACA assignment",
      ],
      accent: "#0A192F",
      icon: "user-round",
    },
    {
      role: "Child",
      name: "Marcus, 8",
      situation:
        "Two grade levels behind in math after disrupted elementary years. After-school hours unsupervised because Jasmine works split shifts.",
      activations: [
        "Aldine ISD Head Start / after-school placement",
        "Pediatric primary care via Harris Health",
        "Free dental + vision through CHIP",
        "Saturday literacy + math tutoring via HCC North practicum",
      ],
      accent: "#8B0000",
      icon: "baby",
    },
    {
      role: "Grandfather",
      name: "Curtis, 71",
      situation:
        "Retired Navy logistics. Helps Jasmine when he can, but uncontrolled diabetes and a recent fall on the apartment stairs have made daily activities dangerous. Skipping insulin due to cost.",
      activations: [
        "VA + Medicare dual-eligible enrollment complete",
        "Home modification: grab bars, threshold ramps, lever handles",
        "SNAP + LIHEAP + Texas Over-65 tax freeze filed",
        "Harris County RIDES scheduled for monthly endocrinology",
      ],
      accent: "#D4AF37",
      icon: "user-2",
    },
  ],
};

// ---------------------- CDBG Alignment · NHD framing ----------------------
export const nhCdbgAlignment = [
  {
    id: 1,
    cityObjective: "Preserve & Expand Affordable Housing",
    cityNeed:
      "Rising rents along the corridor as industrial absorption accelerates. Long-time district residents at displacement risk as warehouse jobs draw in new arrivals.",
    bridgeContribution:
      "Bridge connectors negotiate landlord relationships, coordinate Harris County Housing Authority FSS/Section 8 utilization, and walk district residents into first-time homeownership readiness — anti-displacement, in practice.",
  },
  {
    id: 2,
    cityObjective: "Improve Public Infrastructure in LMI Neighborhoods",
    cityNeed:
      "North Corridor Consolidation Package 3 (Fall 2025 → Winter 2028) brings major traffic-control and pavement work. Sidewalks, lighting, and bus-stop safety remain top resident asks.",
    bridgeContribution:
      "Connectors flag neighborhood-level infrastructure barriers — unsafe walking for seniors, lighting gaps near transit stops — directly back to the Greater Greenspoint Redevelopment Authority. A feedback loop the district currently lacks.",
  },
  {
    id: 3,
    cityObjective: "Support Public Services for Vulnerable Populations",
    cityNeed:
      "Senior nutrition, transportation, youth programs, healthcare access, and disability supports are persistent service gaps documented in Harris County human-services data.",
    bridgeContribution:
      "This is the heart of the Bridge model. One connector. One household. Active referrals across Harris Health, Meals on Wheels, Harris County RIDES, Aldine/Spring ISD after-school, and AAA Houston-Harris — for 12–24 months minimum.",
    primary: true,
  },
  {
    id: 4,
    cityObjective: "Prevent & Address Homelessness",
    cityNeed:
      "Eviction risk rising as corridor rents climb. Coalition for the Homeless of Houston/Harris County reports continued northern-corridor hot spots.",
    bridgeContribution:
      "Bridge intake screens for housing instability on day one. Emergency rental/utility/LIHEAP coordination is built into the connector role — preventing eviction before the 5-day notice ever arrives.",
  },
  {
    id: 5,
    cityObjective: "Promote Economic Empowerment & Self-Sufficiency",
    cityNeed:
      "Corridor employers (Amazon, FedEx, Sysco, Panelmatic) are hiring at scale. The gap is the trained human chain that connects district households to those jobs.",
    bridgeContribution:
      "Direct enrollment into Lone Star North Harris certificates (logistics, CDL, manufacturing, allied health) paired with Workforce Solutions childcare subsidies and Amazon Career Choice — investments already allocated, finally activated through one relationship.",
  },
];

export const nhFactSheet = [
  { v: "67,450", k: "District residents (ACS 5-Yr approx)" },
  { v: "$41,280", k: "Median household income" },
  { v: "26.4%", k: "Overall poverty rate" },
  { v: "62.7%", k: "Hispanic / Latino" },
  { v: "31.5%", k: "Black / African American" },
  { v: "20M+ sq ft", k: "Industrial absorption in district" },
  { v: "2 : 1", k: "Industrial-to-office ratio (district)" },
  { v: "4 mi", k: "From district center to IAH" },
];

// Hero stats ribbon (4 strongest)
export const nhHeroStats = [
  { k: "26.4%", v: "District Poverty Rate" },
  { k: "20M+", v: "Sq Ft Industrial Absorption" },
  { k: "1,000+", v: "New Jobs · Panelmatic Build" },
  { k: "4 mi", v: "From Center to IAH" },
];


// ============================================================
//  Program Goals · 5-Year Forecast (NH-specific outcome targets)
//  Scaled to 1,200 HH (~80% of Baytown's 1,500 HH baseline) AND
//  re-shaped for the three-portfolio NH mix:
//    Agency 1 · Section 8/HCV-Voucher Portfolio        (400 HH)
//    Agency 2 · Corridor-Workforce Portfolio           (400 HH)
//    Agency 3 · General Community + Aging-in-Place     (400 HH)
//  Replaces Baytown's "Lee College" workforce track with
//  Lone Star North Harris + HCC North + Workforce Solutions
//  Greater Houston for the IAH-corridor employer pipeline.
// ============================================================

export const nhProgramGoalsForecast = [
  {
    year: "Year 1",
    households: 1200,
    seniors: 400,
    families: 800,
    lonestarEnrollment: 192,        // LSNH + HCC North cert programs
    lonestarGraduates: 0,           // 12–24 mo certs · no grads in Yr 1
    corridorPlacements: 95,         // Amazon HOU2 / FedEx ZHOU / Sysco / Coca-Cola / Panelmatic
    section8Recerts: 380,           // 95% of 400 vouchers · on-time recertification
    homeMods: 145,                  // Senior home modifications (grab bars, ramps, lighting)
    medicareEnrollment: 340,        // Medicare Savings / Extra Help / LIS enrollments
    benefitsRecovered: 1900000,     // SNAP, LIHEAP, Texas Over-65, Medicare Savings, VA
    evictionsPrevented: 116,        // Pre-eviction stabilization across all 3 portfolios
    agingInPlaceStabilized: 320,    // 80% of seniors achieving aging-in-place plan year-end
    homeownershipTransitions: 0,    // No Section 8 → homeownership conversions in Yr 1
    note: "Launch year: intake, enrollment, and benefits navigation. First LSNH/HCC North cohort enrolls; no graduates yet (12–24 mo certs). Section 8 recertification compliance jumps from district baseline ~78% to 95%+.",
  },
  {
    year: "Year 2",
    households: 1200,
    seniors: 400,
    families: 800,
    lonestarEnrollment: 255,
    lonestarGraduates: 76,          // First cohort completes 12-mo certs
    corridorPlacements: 175,        // First LSNH/HCC grads enter Amazon HOU2 + allied health
    section8Recerts: 388,
    homeMods: 176,                  // Backlog clears as Year-1 connector relationships mature
    medicareEnrollment: 365,
    benefitsRecovered: 2200000,
    evictionsPrevented: 140,
    agingInPlaceStabilized: 355,
    homeownershipTransitions: 0,
    note: "First LSNH/HCC graduates enter IAH-corridor workforce (Amazon HOU2, FedEx Express ZHOU, allied-health at Houston Methodist Willowbrook). Senior home-mod backlog clears.",
  },
  {
    year: "Year 3",
    households: 1200,
    seniors: 400,
    families: 800,
    lonestarEnrollment: 288,
    lonestarGraduates: 132,
    corridorPlacements: 215,
    section8Recerts: 392,
    homeMods: 160,
    medicareEnrollment: 378,
    benefitsRecovered: 2320000,
    evictionsPrevented: 152,
    agingInPlaceStabilized: 372,
    homeownershipTransitions: 12,   // First Section 8 → homeowner conversions
    note: "Steady-state. Corridor placements earning $42k–$58k starting wages (Amazon Tier 1, FedEx PT-to-FT, Sysco delivery, Panelmatic apprentice). First Section 8 voucher-holders transition to homeownership via HCV-Homeownership Option.",
  },
  {
    year: "Year 4",
    households: 1200,
    seniors: 400,
    families: 800,
    lonestarEnrollment: 304,
    lonestarGraduates: 156,
    corridorPlacements: 240,
    section8Recerts: 395,
    homeMods: 156,
    medicareEnrollment: 382,
    benefitsRecovered: 2400000,
    evictionsPrevented: 160,
    agingInPlaceStabilized: 380,
    homeownershipTransitions: 18,
    note: "First Bridge graduates qualify for first-time home purchase (TDHCA + HUD HCV-Homeownership). Sterile Mate transitions to advisory only. Independent evaluator publishes 4-yr cohort outcomes.",
  },
  {
    year: "Year 5",
    households: 1200,
    seniors: 400,
    families: 800,
    lonestarEnrollment: 320,
    lonestarGraduates: 172,
    corridorPlacements: 265,
    section8Recerts: 396,
    homeMods: 152,
    medicareEnrollment: 388,
    benefitsRecovered: 2480000,
    evictionsPrevented: 168,
    agingInPlaceStabilized: 384,
    homeownershipTransitions: 25,
    note: "Full North Houston District operational independence. 5-year cohort of credentialed corridor-workforce graduates, aging-in-place seniors, and first-generation homeowners established. Hub eligible for Regional consolidation across Harris County.",
  },
];

export const nhProgramGoalsTotals = {
  householdYears: 6000,                  // 1,200 HH × 5 yrs of service
  lonestarEnrollmentTotal: 1359,
  lonestarGraduatesTotal: 536,
  corridorPlacementsTotal: 990,
  section8RecertsTotal: 1951,
  homeModsTotal: 789,
  medicareEnrollmentsTotal: 1853,
  benefitsRecoveredTotal: 11300000,
  evictionsPreventedTotal: 736,
  agingInPlaceStabilizedTotal: 1811,
  homeownershipTransitionsTotal: 55,
};

// Program-wide outcome benchmarks — apply across the 5-year horizon.
// Adapted for NH's three-portfolio mix and corridor-workforce orientation.
export const nhProgramOutcomeBenchmarks = [
  {
    metric: "Caseload Cap (Service Connector : Households)",
    target: "1 : 38",
    rationale: "Federal high-touch case-management standard. WCDC Chicago operated at 1:35. Matches Baytown deployment to ensure portfolio fidelity.",
  },
  {
    metric: "Intake → First Service",
    target: "Same day",
    rationale: "Single-intake model. Multi-generational household assessed and enrolled in one 90-minute visit at the Spoke agency.",
  },
  {
    metric: "Service Continuity",
    target: "12–24 months minimum",
    rationale: "Bridge is a stabilization program, not a crisis hotline. Long-tail trust-based relationship is the mechanism that closes outcomes.",
  },
  {
    metric: "Annual Benefits Recovered / Senior",
    target: "$3,000–$6,000",
    rationale: "SNAP, LIHEAP, Texas Over-65 freeze, Medicare Savings, Extra Help, VA screening. Higher per-senior recovery than Baytown given Greenspoint's deeper under-enrollment baseline.",
  },
  {
    metric: "Preventable Senior EMS Reduction",
    target: "≥ 40% over 24 mo",
    rationale: "PCP continuity + chronic-disease management installed within 30 days of intake. Tracked via Harris County EMS dispatch records for 77086/77032/77073 ZIP cohort.",
  },
  {
    metric: "Aging-in-Place Stabilization Rate (Yr 2+)",
    target: "≥ 90% of senior cohort",
    rationale: "Year-end stabilization plan (home-mods + benefits + PCP + caregiver coordination + AAA navigation) in place for 90%+ of enrolled seniors. NH-specific given Agency 3's senior focus.",
  },
  {
    metric: "Lone Star North Harris / HCC North Completion Rate",
    target: "≥ 60% of enrolled cohort",
    rationale: "Childcare subsidy + transit subsidy + connector retention support closes the dropout gap. Tracked via direct LSNH and HCC North registrar data-share MOU.",
  },
  {
    metric: "IAH Corridor Employer Retention (12-mo)",
    target: "≥ 80% at 12 months",
    rationale: "Amazon HOU2, FedEx Express ZHOU, Sysco Houston, Coca-Cola Southwest, Panelmatic, Houston Methodist Willowbrook. Retention measured via employer HR data-share or quarterly wage verification.",
  },
  {
    metric: "Section 8 / HCV Recertification Compliance",
    target: "≥ 98% on-time",
    rationale: "Agency 1 portfolio outcome. Replaces baseline ~78% district-wide compliance rate. Reduces voucher-loss attrition that destabilizes families and burdens HHA admin.",
  },
  {
    metric: "Section 8 → Homeownership Transitions (Yr 3+)",
    target: "12–25 / year by Yr 3–5",
    rationale: "HUD HCV-Homeownership Option + TDHCA TSAHC down-payment assistance + connector-supervised credit repair. Quintessential 'graduation' outcome.",
  },
  {
    metric: "Independent Annual Evaluation",
    target: "Year-end published, public-record",
    rationale: "Third-party academic or professional evaluator (e.g., Rice Kinder Institute, UH Hobby School). Funded by the $25k Hub line item.",
  },
  {
    metric: "Agency Performance Review",
    target: "Annual per Spoke",
    rationale: "5-year contract with annual District Board review. RFP renewal contingent on benchmark performance. Non-performing agency replaced via re-bid.",
  },
];
